// canvasBackgroundStyle — CSS 方式の canvas 背景 (2026-06-20 canvas-background-color §8.4) を
// メイン (`classic/DrawingCanvas.tsx`) とプレビュー (`classic/PreviewOverlay.tsx` /
// `modern/ModernPreviewPanel.tsx`) で共有する pure helper。
//
// ── なぜ存在するか (feature/preview-panel-background 2026-09-28) ───────────────
// メインキャンバスは engine が **透明に描き、背景色は CSS の inline background-color で
// 敷く**方式 (DrawingCanvas.tsx)。プレビューは main canvas を `drawImage` で縮小コピー
// しているだけなので、DOM 上の CSS 背景は `drawImage` の対象外 (= 描画結果ピクセルしか
// コピーされない) で写らない。→ プレビュー側の canvas 要素にも同じ CSS 背景を当てて
// 解消する。
//
// 画素へ焼き込む方式 (fillRect) は取らない: 色変更のたびに再描画が要る上、メイン canvas
// (CSS 方式) と挙動がズレるため。DrawingCanvas と同じ CSS 方式に統一する。
//
// DrawingCanvas.tsx 側の JSX 内 ternary (`backgroundVisible === false ? ... : ...`) は
// `DrawingCanvas.backgroundChecker.smoke.test.ts` がソース文字列レベルで固定している
// ため、そちらは書き換えない (= 挙動を変えずに `rgbToCss` の定義元だけをここへ寄せて
// import する)。プレビュー側 2 実装 (Classic/Modern) の二重実装はこの helper で解消する。

/**
 * rgb24 (0..0xFFFFFF) → CSS `#rrggbb`。DrawingCanvas / LayerPopup / ModernLayerPanel の
 * ■ swatch helper と同形 (= 同じ色変換、見た目一致)。
 */
export const rgbToCss = (rgb: number): string =>
  `#${(rgb & 0xffffff).toString(16).padStart(6, '0')}`;

export interface CanvasBackgroundStyle {
  /** canvas 要素の inline style へ spread する (可視時のみ backgroundColor を持つ)。 */
  readonly style: { readonly backgroundColor?: string };
  /**
   * canvas 要素の className へ追加する token。非可視時のみ `'canvas-bg-checker'`
   * (styles.css の市松ルール、`data-oekaki-canvas` の有無に関わらず効く)、可視時は
   * null (= 追加しない)。
   */
  readonly extraClassName: string | null;
}

/**
 * 背景可視/非可視から canvas 要素へ当てる CSS を計算する。
 *
 * - `backgroundVisible === false` (= 👁 off、透過確認): inline background は当てず、
 *   呼び出し側が `canvas-bg-checker` class (styles.css の市松ルール) を追加する。
 * - それ以外 (true / 未指定): `backgroundRgb` (未指定なら白 0xffffff) を inline
 *   `background-color` に変換して当てる。
 */
export const canvasBackgroundStyle = (
  backgroundVisible: boolean | undefined,
  backgroundRgb: number | undefined,
): CanvasBackgroundStyle =>
  backgroundVisible === false
    ? { style: {}, extraClassName: 'canvas-bg-checker' }
    : { style: { backgroundColor: rgbToCss(backgroundRgb ?? 0xffffff) }, extraClassName: null };
